import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, Send, X, Inbox } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  report_approved: CheckCircle2,
  report_returned: Send,
};

const ICON_CLS = {
  report_approved: "text-green-600",
  report_returned: "text-amber-600",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  const refreshCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    refreshCount();
    const iv = setInterval(refreshCount, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleClick = async (n) => {
    try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* silent */ }
    if (n.link) nav(n.link);
    setOpen(false);
    refreshCount();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  return (
    <div ref={popRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost relative"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[480px] bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white shadow-xl z-50 flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-neutral-700">
            <div>
              <p className="overline">Notifications</p>
              <div className="font-heading font-black text-sm">{unread > 0 ? `${unread} non lue(s)` : "Tout est à jour"}</div>
            </div>
            <div className="flex gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-[#002FA7] hover:underline px-2"
                  data-testid="mark-all-read"
                >
                  Tout marquer lu
                </button>
              )}
              <button className="btn-ghost" onClick={() => setOpen(false)}>
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-2">
                <Inbox size={32} className="opacity-40" />
                <div className="text-sm">Aucune notification</div>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-neutral-800">
                {items.map((n) => {
                  const Icon = ICONS[n.type] || Bell;
                  const iconCls = ICON_CLS[n.type] || "text-gray-400";
                  return (
                    <li key={n.notification_id}>
                      <button
                        type="button"
                        onClick={() => handleClick(n)}
                        className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-neutral-800 flex gap-3 transition-colors ${!n.read ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}
                        data-testid={`notification-item-${n.notification_id}`}
                      >
                        <Icon size={18} className={`${iconCls} shrink-0 mt-0.5`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-semibold text-sm truncate">{n.title}</div>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
                          </div>
                          {n.message && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-3 whitespace-pre-wrap">
                              {n.message}
                            </div>
                          )}
                          <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">{timeAgo(n.created_at)}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
